import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
} from '@material-ui/core';
import "../css/Modal.css";
import "../css/DropDown.css";
import { TiDelete } from "react-icons/ti";
import { withRouter } from './withRouter';
import { connect } from 'react-redux';
import moment from 'moment';
import DynamicNotificationsForm from './DynamicNotificationsForm';
import { getNotificationHistory, getNotificationSubscribers } from '../Path/Path';

const tabBtn = (active) => ({
    flex: 1,
    padding: '8px 12px',
    cursor: 'pointer',
    border: 'none',
    borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
    background: 'transparent',
    fontWeight: active ? 700 : 500,
    color: active ? '#1976d2' : '#555',
    fontSize: 13,
});
const cell = { padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 12, textAlign: 'left', verticalAlign: 'top' };
const th = { ...cell, fontWeight: 700, background: '#fafafa', position: 'sticky', top: 0 };
const fmt = (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : '—');
const shortTok = (t) => (t ? `${t.slice(0, 18)}…${t.slice(-6)}` : '');

// ---- Sent history (with expandable recipients) ----
const HistoryView = ({ appId }) => {
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState({});
    useEffect(() => {
        let alive = true;
        getNotificationHistory(appId).then((d) => alive && setRows(Array.isArray(d) ? d : [])).catch(() => alive && setRows([]));
        return () => { alive = false; };
    }, [appId]);
    if (rows === null) return <p style={{ padding: 12, fontSize: 13 }}>Loading history…</p>;
    if (!rows.length) return <p style={{ padding: 12, fontSize: 13 }}>No notifications have been sent for this app yet.</p>;
    return (
        <div style={{ maxHeight: 380, overflow: 'auto' }}>
            <p style={{ fontSize: 12, color: '#777', margin: '4px 0 8px' }}>{rows.length} sent · click a row to see who received it</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                    <th style={th}>Message</th><th style={th}>Scheduled</th><th style={th}>Sent at</th>
                    <th style={th}>Delivered</th><th style={th}>Failed</th><th style={th}>Type</th>
                </tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <React.Fragment key={r._id}>
                            <tr onClick={() => setOpen((o) => ({ ...o, [r._id]: !o[r._id] }))} style={{ cursor: 'pointer' }}>
                                <td style={cell}>{r.message}</td>
                                <td style={cell}>{r.source === 'manual' ? 'Immediate' : fmt(r.scheduledAt)}</td>
                                <td style={cell}>{fmt(r.sentAt)}</td>
                                <td style={{ ...cell, color: '#2e7d32', fontWeight: 700 }}>{r.sentCount ?? 0}</td>
                                <td style={{ ...cell, color: (r.failedCount ? '#c62828' : '#999') }}>{r.failedCount ?? 0}</td>
                                <td style={cell}>{r.source || 'scheduled'}</td>
                            </tr>
                            {open[r._id] && (
                                <tr><td style={{ ...cell, background: '#fafafa' }} colSpan={6}>
                                    <strong style={{ fontSize: 12 }}>Recipients ({(r.recipients || []).length}):</strong>
                                    <div style={{ maxHeight: 140, overflow: 'auto', marginTop: 4 }}>
                                        {(r.recipients || []).length === 0 ? <em style={{ fontSize: 12 }}>No device tokens were registered at send time.</em> :
                                            (r.recipients || []).map((rc, i) => (
                                                <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', color: rc.status === 'ok' ? '#2e7d32' : '#c62828' }}>
                                                    {rc.status === 'ok' ? '✓' : '✗'} {shortTok(rc.token)}
                                                </div>
                                            ))}
                                    </div>
                                </td></tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ---- Notification subscribers (AppsUsers) ----
const SubscribersView = ({ appId }) => {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        let alive = true;
        getNotificationSubscribers(appId).then((d) => alive && setRows(Array.isArray(d) ? d : [])).catch(() => alive && setRows([]));
        return () => { alive = false; };
    }, [appId]);
    if (rows === null) return <p style={{ padding: 12, fontSize: 13 }}>Loading subscribers…</p>;
    if (!rows.length) return <p style={{ padding: 12, fontSize: 13 }}>No devices have subscribed to notifications for this app yet.</p>;
    return (
        <div style={{ maxHeight: 380, overflow: 'auto' }}>
            <p style={{ fontSize: 12, color: '#777', margin: '4px 0 8px' }}>{rows.length} device(s) subscribed</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>#</th><th style={th}>Device push token</th><th style={th}>Subscribed</th></tr></thead>
                <tbody>
                    {rows.map((u, i) => (
                        <tr key={u._id || i}>
                            <td style={cell}>{i + 1}</td>
                            <td style={{ ...cell, fontFamily: 'monospace', fontSize: 11 }}>{shortTok(u.token)}</td>
                            <td style={cell}>{fmt(u.createdAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ManageNotificationsModal = ({ open, onClick, data }) => {
    const [tab, setTab] = useState('schedule');
    const appId = data?.appId;

    useEffect(() => { setTab('schedule'); }, [data, open]);

    const onCancel = () => onClick();

    return (
        <Dialog aria-labelledby='dialog-title' aria-describedby='dialog-description' open={open} maxWidth="md" fullWidth>
            <div className='close-btn' onClick={onClick} style={{ cursor: "pointer" }}><TiDelete /></div>
            <DialogTitle id="dialog-title">
                <h2>{"Manage App Notifications"}</h2>
                {data?.appName ? <div style={{ fontSize: 13, color: '#777' }}>{data.appName}</div> : null}
                <div className='line'></div>
            </DialogTitle>
            <DialogContent>
                <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 8 }}>
                    <button style={tabBtn(tab === 'schedule')} onClick={() => setTab('schedule')}>Schedule</button>
                    <button style={tabBtn(tab === 'history')} onClick={() => setTab('history')}>Sent History</button>
                    <button style={tabBtn(tab === 'subscribers')} onClick={() => setTab('subscribers')}>Subscribers</button>
                </div>
                <div className="modal-main">
                    {tab === 'schedule' && <DynamicNotificationsForm data={data} onCancel={onCancel} />}
                    {tab === 'history' && appId && <HistoryView appId={appId} />}
                    {tab === 'subscribers' && appId && <SubscribersView appId={appId} />}
                </div>
            </DialogContent>
        </Dialog>
    )
}

const mapStatetoProps = (state) => {
    return {
        user: state.user,
        lang: state.lang
    }
}

export default connect(mapStatetoProps)(withRouter(ManageNotificationsModal));
